import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ENetworkRequestStatus } from 'services/BackendApi';
import {
  isInternalUser as isSrSelector,
  isAdmin as isAdminSelector,
  isFinanceUser as isFinanceUserSelector,
} from 'store/modules/auth/selectors';
import { IInvoiceAddressee, IInvoiceDueDate, IPaymentMethod } from 'services/BookingManagerApi/types';
import { EBankAccount } from 'interfaces';
import * as BreakdownActions from 'store/modules/bookingManager/subdomains/breakdown/actions';
import {
  EInvoiceAddresseeType,
  EInvoiceMutationMode,
  EInvoiceLang,
  EProductOrdering,
} from 'store/modules/bookingManager/subdomains/breakdown/model';
import { ProductOrderingSelect } from './ProductOrderingSelect';
import { breakdownNetworkRequestsSelector } from 'store/modules/bookingManager/subdomains/breakdown/selectors';
import { enqueueNotification } from 'store/modules/ui';
import { formatPrice, roundToDecimalPlaces } from 'utils';
import { Radio } from 'ui/Radio';
import FluidButton from 'ui/FluidButton';
import { edit } from 'ui/Icons';
import { GeneralModal } from 'ui/GeneralModal';
import { ModalHeading } from 'ui/GeneralModal/ModalHeading';
import { InvoiceAddresseeText } from './InvoiceAddresseeText';
import SingleSelect from 'ui/SingleSelect';
import { InvoiceModalDueDateRow } from 'ui/InvoiceModalDueDateRow';
import { SvgIcon } from 'ui/SvgIcon';
import ExclamationCircleIcon from 'ui/Icons/tabler/exclamation-circle.component.svg';
import { InvoiceTPOrClient } from './InvoiceTPOrClient';
import Checkbox from 'ui/Checkbox';
import Editor from 'pureUi/Editor';
import { VerticalSpace } from 'ui/VerticalSpace';
import { HtmlStringRenderer } from 'components/HtmlStringRenderer';

export interface IManualInvoiceModal {
  isOpen: boolean;
  mode: EInvoiceMutationMode | null;
  invoiceAddresseeType: EInvoiceAddresseeType;
  invoiceAddressee?: IInvoiceAddressee;
  invoiceAddresseeLoad: ENetworkRequestStatus;
  selectedBankAccount: EBankAccount;
  lang: EInvoiceLang;
  currencyMismatch: boolean;
  totalCostToClientCents: number;
  SubtotalCents: number;
  bookingCurrencySymbol: string;
  paymentTerms: string;
  cancellationPolicies: string;
  paymentMethods: IPaymentMethod[];
  marginPercentage?: number;
}

export const getNewAmountFromPercentageOfOriginal = (initial: number, percentage: number) => {
  return Math.round((initial / 100) * percentage);
};

// eslint-disable-next-line react/display-name
export const ManualInvoiceModal: React.FC<IManualInvoiceModal> = React.memo(props => {
  const dispatch = useDispatch();
  const breakdownNetworkRequests = useSelector(breakdownNetworkRequestsSelector);

  const isSr = useSelector(isSrSelector);
  const isAdmin = useSelector(isAdminSelector);
  const isFinanceUser = useSelector(isFinanceUserSelector);
  const isSupervisor = isSr || isAdmin || isFinanceUser;

  const { isOpen } = props;

  const initialBalance =
    props.invoiceAddresseeType === EInvoiceAddresseeType.FINAL_CLIENT
      ? props.totalCostToClientCents
      : props.SubtotalCents;

  const [invoiceDueDates, setInvoiceDueDates] = useState<IInvoiceDueDate[]>([
    { date: null, percentage: 100, amountCents: initialBalance, isConfirmed: false },
  ]);
  const [outstandingBalanceCents, setOutstandingBalanceCents] = useState<number>(initialBalance);
  const [isAllItem, setIsAllItem] = useState<boolean>(true);
  const [description, setDescription] = useState<string | undefined>(undefined);
  const [orderingType, setOrderingType] = useState<EProductOrdering>(EProductOrdering.BREAKDOWN);
  // 6925
  const [amendedCancellationPolicies, setAmendedCancellationPolicies] = useState<string>(props.cancellationPolicies);
  const [isCpCheckTicked, setIsCpCheckTicked] = useState<boolean>(false);

  const handleGenerateInvoiceClick = useCallback(() => {
    dispatch(
      BreakdownActions.generateManualInvoiceRequestAction(
        props.selectedBankAccount,
        invoiceDueDates.filter(d => d.isConfirmed),
        amendedCancellationPolicies,
        description,
        orderingType
      )
    );
  }, [dispatch, props.selectedBankAccount, invoiceDueDates, amendedCancellationPolicies, description, orderingType]);

  const handleCloseClick = useCallback(() => {
    dispatch(BreakdownActions.closeCreateManualInvoiceModalAction());

    if (props.mode === EInvoiceMutationMode.UPDATE) {
      dispatch(
        enqueueNotification({
          message: 'The breakdown was not updated. Please save new invoice to confirm changes',
          options: { variant: 'warning' },
        })
      );
    }
  }, [dispatch, props.mode]);

  const balance = useMemo(() => {
    return props.invoiceAddresseeType === EInvoiceAddresseeType.FINAL_CLIENT
      ? props.totalCostToClientCents
      : props.SubtotalCents;
  }, [props.invoiceAddresseeType, props.totalCostToClientCents, props.SubtotalCents]);

  // Recalculate due date amounts when balance changes (e.g. addressee type switch)
  useEffect(() => {
    setInvoiceDueDates(prev =>
      prev.map(d => ({
        ...d,
        amountCents: Math.round(((d.percentage || 0) / 100) * balance),
      }))
    );
  }, [balance]);

  // Track outstanding balance (balance minus confirmed due dates)
  useEffect(() => {
    const amountLeftToCover = invoiceDueDates
      .filter(idd => idd.isConfirmed)
      .reduce((acum, curr) => {
        acum -= curr.amountCents || 0;
        return acum;
      }, balance);
    setOutstandingBalanceCents(amountLeftToCover);
  }, [invoiceDueDates, balance]);

  const handleSetBankAccount = useCallback(
    bankAccount => {
      dispatch(BreakdownActions.setManualInvoiceBankAccountAction(bankAccount));
    },
    [dispatch]
  );

  const handleSetTravelPartnerInvoice = useCallback(() => {
    if (props.invoiceAddresseeType !== EInvoiceAddresseeType.TRAVEL_PARTNER) {
      dispatch(BreakdownActions.setManualInvoiceAddresseeTypeAction(EInvoiceAddresseeType.TRAVEL_PARTNER));
      dispatch(BreakdownActions.getManualInvoiceAddresseeRequestAction());
    }
  }, [dispatch, props.invoiceAddresseeType]);

  const handleSetFinalClientInvoice = useCallback(() => {
    if (props.invoiceAddresseeType !== EInvoiceAddresseeType.FINAL_CLIENT) {
      dispatch(BreakdownActions.setManualInvoiceAddresseeTypeAction(EInvoiceAddresseeType.FINAL_CLIENT));
      dispatch(BreakdownActions.getManualInvoiceAddresseeRequestAction());
    }
  }, [dispatch, props.invoiceAddresseeType]);

  const handleEditAddressee = useCallback(() => {
    dispatch(BreakdownActions.openAddresseeManualModalAction());
  }, [dispatch]);

  const handleSetIsAll = useCallback(() => {
    setIsAllItem(true);
    setDescription(undefined);
  }, []);

  const handleResetIsAll = useCallback(() => {
    setIsAllItem(false);
  }, []);

  const addInvoiceDueDate = () => {
    const percentageLeft = invoiceDueDates.reduce((acum, cur) => {
      acum -= cur.percentage || 0;
      return acum;
    }, 100);

    const amountCentsLeft = invoiceDueDates.reduce((acum, cur) => {
      acum -= cur.amountCents || 0;
      return acum;
    }, balance);

    setInvoiceDueDates(prev => [
      ...prev,
      { date: null, percentage: percentageLeft, amountCents: amountCentsLeft, isConfirmed: false },
    ]);
  };

  const handleInvoiceDueDateUpdateAmountCents = (index: number, newAmountCents: number) => {
    const newInvoiceDueDates = [...invoiceDueDates];
    const newPercentage = (newAmountCents * 100) / balance;
    newInvoiceDueDates[index].amountCents = newAmountCents;
    newInvoiceDueDates[index].percentage = roundToDecimalPlaces(newPercentage, 2);
    setInvoiceDueDates(newInvoiceDueDates);
  };

  const handleInvoiceDueDateUpdatePercentage = (index: number, percentage: number) => {
    const newInvoiceDueDates = [...invoiceDueDates];
    newInvoiceDueDates[index].percentage = percentage;
    newInvoiceDueDates[index].amountCents = getNewAmountFromPercentageOfOriginal(balance, percentage);
    setInvoiceDueDates(newInvoiceDueDates);
  };

  const handleInvoiceDueDateUpdateDate = (index: number, newDate: string) => {
    const newInvoiceDueDates = [...invoiceDueDates];
    newInvoiceDueDates[index].date = newDate;
    setInvoiceDueDates(newInvoiceDueDates);
  };

  const handleInvoiceDueDateRemove = (indexToRemove: number) => {
    setInvoiceDueDates(prev => prev.filter((_, i) => i !== indexToRemove));
  };

  const handleInvoiceDueDateConfirm = (indexToConfirm: number) => {
    const newInvoiceDueDates = [...invoiceDueDates];
    newInvoiceDueDates[indexToConfirm].isConfirmed = true;
    setInvoiceDueDates(newInvoiceDueDates);
  };

  const paymentMethodOptions = useMemo(
    () =>
      props.paymentMethods.map(x => ({
        value: x.name,
        label: x.name,
      })),
    [props.paymentMethods]
  );

  const selectedPaymentMethod = useMemo(() => props.paymentMethods.find(x => x.name === props.selectedBankAccount), [
    props.paymentMethods,
    props.selectedBankAccount,
  ]);

  const generateDisabled = useMemo(() => {
    const firstCase = balance <= 0; // total amount is 0 or lower
    const dueDatesEmpty = invoiceDueDates.length === 0; // must have at least one due date
    const outstandingNotMatched = isAllItem && balance > 0 && outstandingBalanceCents !== 0; // due dates must cover full outstanding balance (skipped for partial)
    const hasUnconfirmed = invoiceDueDates.some(d => !d.isConfirmed); // all rows must be confirmed
    const fifthCase = !isAllItem ? description === undefined : false; // partial requires description

    // 6925 - if the final client is selected, the user must tick the checkbox
    const sixthCase = props.invoiceAddresseeType === EInvoiceAddresseeType.FINAL_CLIENT && !isCpCheckTicked;

    // OWA-7676 - a bank account must be selected and it might not have been selected by default
    const seventhCase = !props.selectedBankAccount;

    return (
      !!firstCase ||
      !!dueDatesEmpty ||
      !!outstandingNotMatched ||
      !!hasUnconfirmed ||
      !!fifthCase ||
      !!sixthCase ||
      !!seventhCase
    );
  }, [
    invoiceDueDates,
    outstandingBalanceCents,
    balance,
    description,
    isAllItem,
    props.invoiceAddresseeType,
    isCpCheckTicked,
    props.selectedBankAccount,
  ]);

  if (!isOpen) {
    return null;
  }

  return (
    <GeneralModal
      onClose={handleCloseClick}
      modalWindowClassName="w-778px px-35px py-25px rounded"
      shouldCloseByClickingOutside
    >
      <ModalHeading>Create Manual Invoice</ModalHeading>

      <InvoiceTPOrClient
        edit={edit}
        handleEditAddressee={handleEditAddressee}
        handleSetFinalClientInvoice={handleSetFinalClientInvoice}
        handleSetTravelPartnerInvoice={handleSetTravelPartnerInvoice}
        invoiceAddresseeType={props.invoiceAddresseeType}
        isSupervisor={isSupervisor}
        invoiceType="manual"
      />

      <InvoiceAddresseeText
        invoiceAddressee={props.invoiceAddressee}
        invoiceAddresseeLoad={props.invoiceAddresseeLoad}
      />

      <SingleSelect
        fieldId="invoice-payment-method"
        label="Payment Method"
        className="invoice-payment-method max-w-[730px] mt-[30px]"
        labelClassName="text-[16px] leading-[21px] font-bold"
        value={props.selectedBankAccount}
        options={paymentMethodOptions}
        onChange={handleSetBankAccount}
        maxVisibleItems={6}
      />

      {!!selectedPaymentMethod?.warning && (
        <div className="mt-[5px] mb-5 min-h-[100px] max-h-[100px] overflow-y-scroll max-w-fit border border-solid border-gray-20 rounded p-4 font-pt-sans leading-xs text-13px text-black">
          {selectedPaymentMethod.warning}
        </div>
      )}

      {props.currencyMismatch && (
        <p className="text-red-95 text-15px leading-sm font-pt-sans mt-[5px]">
          Warning: You have selected a bank account with a different currency to the booking.
        </p>
      )}

      <p className="font-pt-sans font-bold text-base leading-sm text-black mt-[30px] mb-4">Items</p>
      <div className="flex">
        <label className="mr-6">
          <Radio checked={isAllItem === true} onClick={handleSetIsAll} />
          <span className="ml-2">All</span>
        </label>

        <label className="mr-6">
          <Radio checked={isAllItem === false} onClick={handleResetIsAll} />
          <span className="ml-2">Partial</span>
        </label>
      </div>

      {!isAllItem && (
        <>
          <VerticalSpace height="12px" />
          <Editor
            options={[]}
            text={description ? description : ''}
            handleEditorChange={content => {
              setDescription(content);
            }}
          />
        </>
      )}
      {isAllItem && <ProductOrderingSelect value={orderingType} onChange={setOrderingType} />}

      <p className="font-pt-sans font-bold text-base leading-sm text-black mt-[30px] mb-4">Due Dates</p>
      <div className="mb-4 font-pt-sans">
        <div className="flex justify-between items-center bg-brown-10 p-2 px-4">
          <span className="text-sm uppercase">Outstanding Balance</span>
          <span className="font-bold">
            {props.bookingCurrencySymbol}
            {formatPrice(balance / 100)}
          </span>
        </div>

        <div
          style={{
            maxHeight: '155px',
            overflowY: 'auto',
          }}
        >
          {invoiceDueDates.map((invoiceDueDate, index) => (
            <InvoiceModalDueDateRow
              className="p-2 px-4"
              key={index}
              index={index}
              date={invoiceDueDate.date || null}
              allOtherDates={invoiceDueDates
                .filter((x, i) => i !== index && x.date !== null)
                .map(x => x.date || '')}
              percentage={invoiceDueDate.percentage || 0}
              amountCents={invoiceDueDate.amountCents || 0}
              currencySymbol={props.bookingCurrencySymbol}
              isConfirmed={invoiceDueDate.isConfirmed}
              onUpdateAmountCents={(idx, newAmountCents) => {
                handleInvoiceDueDateUpdateAmountCents(idx, newAmountCents);
              }}
              onUpdatePercentage={(idx, newPercent) => {
                handleInvoiceDueDateUpdatePercentage(idx, newPercent);
              }}
              onUpdateDate={(idx, newDate) => {
                handleInvoiceDueDateUpdateDate(idx, newDate);
              }}
              onClickRemove={idx => {
                handleInvoiceDueDateRemove(idx);
              }}
              onClickDelete={idx => {
                handleInvoiceDueDateRemove(idx);
              }}
              onClickConfirm={idx => {
                handleInvoiceDueDateConfirm(idx);
              }}
            />
          ))}
        </div>

        {balance > 0 && (
          <div className="flex justify-left p-2 px-4 items-center bg-brown-10">
            <FluidButton type="secondary" onClick={addInvoiceDueDate} disabled={outstandingBalanceCents <= 0}>
              Add Due Date
            </FluidButton>
            <div className="message">
              {invoiceDueDates.length <= 0 && (
                <span className="ml-2 text-brown-100 flex items-center gap-1">
                  <SvgIcon IconComponent={ExclamationCircleIcon} className="w-4 h-4 fill-brown-100" />
                  <span>You must add at least one Due Date to Generate Manual Invoice</span>
                </span>
              )}
              {invoiceDueDates.length >= 1 && outstandingBalanceCents > 0 && (
                <span className="ml-2 text-brown-100">
                  <span className="font-bold">
                    {props.bookingCurrencySymbol}
                    {formatPrice(outstandingBalanceCents / 100)}
                  </span>{' '}
                  to match outstanding balance.
                </span>
              )}
              {invoiceDueDates.length >= 1 && outstandingBalanceCents === 0 && (
                <span className="ml-2 text-brown-100">
                  <span className="font-bold">100% match.</span> You can&apos;t add more Due Dates.
                </span>
              )}
              {invoiceDueDates.length >= 1 && outstandingBalanceCents < 0 && (
                <span className="ml-2 text-red-100">
                  <span className="font-bold">
                    {props.bookingCurrencySymbol}
                    {formatPrice(outstandingBalanceCents / 100)}
                  </span>
                  <span> Please delete one or more Due Dates.</span>
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {props.invoiceAddresseeType === EInvoiceAddresseeType.FINAL_CLIENT && (
        <div className="mb-4 space-y-5px">
          <span className="font-pt-sans block font-bold text-[16px] leading-20px ">Cancellation Policies</span>

          <div className="font-pt-sans text-13px ">
            <span className="block">
              The final client margin for this booking is{' '}
              <span className="font-bold text-16px">{props.marginPercentage || 0}%</span>.
            </span>
          </div>

          <Editor
            options={[]}
            text={amendedCancellationPolicies}
            handleEditorChange={content => {
              setAmendedCancellationPolicies(content);
            }}
          />

          <label className="font-pt-sans flex items-center text-black text-13px space-x-6px">
            <Checkbox
              checked={isCpCheckTicked}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setIsCpCheckTicked(e.target.checked);
              }}
            />
            <span className="block">
              I have read the cancellation policy and included the corresponding margin where necessary
            </span>
          </label>
        </div>
      )}

      {props.totalCostToClientCents > 0 && (
        <div className="border border-solid border-gray-40 font-pt-sans text-[13px] leading-[18px] p-2 text-black mb-6 opacity-70">
          <span className="block font-bold">Payment Terms</span>
          <span className="block">
            <HtmlStringRenderer content={props.paymentTerms} />
          </span>
        </div>
      )}

      <div className="flex items-center justify-between">
        <FluidButton
          type="primary"
          fixedWidth="220px"
          isLoading={breakdownNetworkRequests.createManualInvoice === ENetworkRequestStatus.PENDING}
          onClick={handleGenerateInvoiceClick}
          disabled={generateDisabled}
        >
          Generate Manual Invoice
        </FluidButton>
        {/* <div className="lang flex items-center">
          <div className="label font-pt-sans text-13px text-black max-w-70px">Invoice language:</div>
          <Multiselect
            className="min-w-120px bg-ivory"
            itemsClassname="bg-ivory"
            onUpdate={handleLang}
            selectedValues={[props.lang]}
            hideCheckboxes={true}
            isSingleSelectMode={true}
            isCloseOnSelect={true}
            options={langOptions}
            dropdownPosition="top"
          />
        </div> */}
      </div>
    </GeneralModal>
  );
});

export default ManualInvoiceModal;
